/**
 * /dashboard/hiring — Hiring hub, Pipeline (a.k.a. Requisitions) view
 * (Phase 3d — 2026-06-01).
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/hiring/page.tsx flattened with
 * the old `layout.tsx` into one route that uses HiringModuleShell.
 */

import { createFileRoute, Navigate } from "@tanstack/react-router";
import { HiringModuleShell } from "@/components/shared/hiring-module-shell";
import { HiringFeatureGate } from "@/components/hiring/hiring-feature-gate";
import { RequisitionsTab } from "@/components/hiring/requisitions-tab";
import { isHiringHrAdmin } from "@/components/hiring/hr-admin-gate";
import { useAuth } from "@/lib/auth";
import { hiringKeys, useRequisitions } from "@/hooks/use-hiring";
import { MANAGER_PLUS } from "@/lib/nav";
import { directoryKeys } from "@/hooks/use-directory";
import { directoryApi, hiringApi } from "@/lib/api";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/hiring/")({
  // A9 hover-prefetch. Primary = the unfiltered requisitions list —
  // RequisitionsTab's initial "all" bucket (role-scoped server-side).
  // The active directory (hiring-manager names) is secondary →
  // fire-and-forget.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery({
      queryKey: directoryKeys.list(false),
      queryFn: ({ signal }) =>
        directoryApi.listDirectory({ includeInactive: false }, signal),
    });
    return context.queryClient
      .ensureQueryData({
        queryKey: hiringKeys.requisitions(undefined),
        queryFn: ({ signal }) => hiringApi.listRequisitions(undefined, signal),
      })
      .catch(() => undefined);
  },
  component: HiringHubRoute,
});

function HiringHubRoute() {
  const { user } = useAuth();
  const { tenantSlug } = Route.useParams();
  const isAdmin = isHiringHrAdmin(user?.role);
  // Jobs is in the nav for managers and HR only. Anyone else who opens
  // /hiring and hires for no job (an interviewer, an ICC member) goes to
  // their interviews instead of an empty page; someone who is a job's
  // hiring manager keeps the page (the server scopes the list to them).
  const outsideNav = !!user?.role && !MANAGER_PLUS.includes(user.role);
  const reqs = useRequisitions(undefined, outsideNav);
  if (outsideNav && reqs.data && reqs.data.requisitions.length === 0) {
    return <Navigate to="/app/$tenantSlug/interviews" params={{ tenantSlug }} replace />;
  }
  return (
    <HiringModuleShell
      title="Jobs"
      description={
        isAdmin
          ? "Track open roles, candidates and interviews end to end."
          : "Your interviews and the roles you're hiring for."
      }
    >
      <HiringFeatureGate>
        <RequisitionsTab />
      </HiringFeatureGate>
    </HiringModuleShell>
  );
}
